import { useEffect, useState, useMemo, useCallback } from 'react';
import { X, Settings, Search, Plus, Trash2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import { frameOrderApi } from '../../services/frameOrderApi';
import { SearchableSelect } from '../ui/SearchableSelect';
import type { CustomerExclude, CustomerSearchResult } from '../../types/frameOrders';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    companies: Array<{ code: string; name: string }>;
    canEdit: boolean;
}

export function FrameOrderExclusionModal({ isOpen, onClose, companies, canEdit }: Props) {
    const [excludes, setExcludes] = useState<CustomerExclude[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null); // customerId being added/removed

    const [companyCode, setCompanyCode] = useState('');
    const [term, setTerm] = useState('');
    const [results, setResults] = useState<CustomerSearchResult[]>([]);
    const [searching, setSearching] = useState(false);

    const companyOpts = useMemo(() => companies.map(c => ({ id: c.code, name: `${c.name} (${c.code})` })), [companies]);

    const loadExcludes = useCallback(() => {
        setLoading(true);
        setError(null);
        frameOrderApi.getCustomerExcludes()
            .then(setExcludes)
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    // Load the current exclusion list whenever the modal opens.
    useEffect(() => {
        if (!isOpen) return;
        loadExcludes();
        setCompanyCode('');
        setTerm('');
        setResults([]);
    }, [isOpen, loadExcludes]);

    // Escape closes.
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    // Debounced customer search (needs a company + >=2 chars).
    useEffect(() => {
        if (!isOpen) return;
        if (!companyCode || term.trim().length < 2) {
            setResults([]);
            setSearching(false);
            return;
        }
        setSearching(true);
        const handle = setTimeout(() => {
            frameOrderApi.searchCustomers(companyCode, term.trim())
                .then(setResults)
                .catch(() => setResults([]))
                .finally(() => setSearching(false));
        }, 300);
        return () => clearTimeout(handle);
    }, [isOpen, companyCode, term]);

    const excludedIds = useMemo(() => new Set(excludes.map(e => e.CustomerID)), [excludes]);

    const grouped = useMemo(() => {
        const map = new Map<string, CustomerExclude[]>();
        for (const e of excludes) {
            const list = map.get(e.CompanyCode) ?? [];
            list.push(e);
            map.set(e.CompanyCode, list);
        }
        return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [excludes]);

    const handleAdd = async (c: CustomerSearchResult) => {
        setBusy(c.CustomerID);
        setError(null);
        try {
            await frameOrderApi.addCustomerExclude({
                customerId: c.CustomerID,
                companyCode: c.CompanyCode,
                customerCode: c.CustomerCode,
                customerName: c.CustomerName,
            });
            loadExcludes();
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setBusy(null);
        }
    };

    const handleRemove = async (customerId: string) => {
        setBusy(customerId);
        setError(null);
        try {
            await frameOrderApi.deleteCustomerExclude(customerId);
            setExcludes(prev => prev.filter(e => e.CustomerID !== customerId));
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setBusy(null);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />

            <div className="relative z-10 mx-4 flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <div className="flex items-center gap-2">
                        <Settings className="h-5 w-5 text-gray-500" />
                        <h2 className="text-lg font-semibold text-gray-900">Excluded Customers</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    <p className="mb-4 text-sm text-gray-500">
                        Customers added here are hidden from the frame order list and the statistics tiles.
                        Search per company to add customers.
                    </p>

                    {error && (
                        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {/* Add section (edit only) */}
                    {canEdit && (
                        <div className="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,14rem)_1fr]">
                                <SearchableSelect
                                    options={companyOpts}
                                    value={companyCode}
                                    onChange={setCompanyCode}
                                    placeholder="Select company"
                                />
                                <div className="relative">
                                    <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={term}
                                        onChange={e => setTerm(e.target.value)}
                                        disabled={!companyCode}
                                        placeholder={companyCode ? 'Search customer name or number...' : 'Select a company first'}
                                        className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    />
                                </div>
                            </div>

                            {/* Results */}
                            {companyCode && term.trim().length >= 2 && (
                                <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white">
                                    {searching && (
                                        <p className="px-3 py-3 text-center text-xs text-gray-400">Searching…</p>
                                    )}
                                    {!searching && results.length === 0 && (
                                        <p className="px-3 py-3 text-center text-xs text-gray-400">No matches</p>
                                    )}
                                    {!searching && results.map(c => {
                                        const already = excludedIds.has(c.CustomerID);
                                        return (
                                            <div
                                                key={c.CustomerID}
                                                className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 last:border-b-0"
                                            >
                                                <div className="min-w-0">
                                                    <div className="truncate text-sm text-gray-900">{c.CustomerName}</div>
                                                    <div className="text-xs text-gray-400">
                                                        {c.CustomerCode}{c.City ? ` · ${c.City}` : ''}
                                                    </div>
                                                </div>
                                                {already ? (
                                                    <span className="shrink-0 text-xs text-gray-400">Excluded</span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleAdd(c)}
                                                        disabled={busy === c.CustomerID}
                                                        className="inline-flex shrink-0 items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                                                    >
                                                        <Plus className="h-3.5 w-3.5" /> Add
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Current exclusion list */}
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        </div>
                    ) : excludes.length === 0 ? (
                        <p className="py-8 text-center text-sm text-gray-400">No customers are excluded.</p>
                    ) : (
                        <div className="space-y-4">
                            {grouped.map(([company, list]) => (
                                <div key={company}>
                                    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {company} ({list.length})
                                    </h3>
                                    <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
                                        {list.map(e => (
                                            <div key={e.CustomerID} className="flex items-center justify-between gap-2 px-3 py-2">
                                                <div className="min-w-0">
                                                    <div className="truncate text-sm text-gray-900">
                                                        {e.CustomerName ?? e.CustomerCode}
                                                    </div>
                                                    <div className="truncate text-xs text-gray-400">
                                                        {e.CustomerCode}{e.Reason ? ` · ${e.Reason}` : ''}
                                                    </div>
                                                </div>
                                                {canEdit && (
                                                    <button
                                                        onClick={() => handleRemove(e.CustomerID)}
                                                        disabled={busy === e.CustomerID}
                                                        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" /> Remove
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
